import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {UsersService} from '../../../services/users/users.service';
import {SessionService} from '../../../services/session/session.service';
import {RecordedUserProduct, RecordedUserProductsResponse} from './user-products.model';

@Component({
    selector: 'app-user-products',
    imports: [CommonModule, MatButtonModule],
    templateUrl: './user-products.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./user-products.component.scss']
})
export class UserProductsComponent implements OnChanges, OnDestroy {
  @Input() userId = '';
  public state: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  public products: RecordedUserProduct[] = [];
  private generation = 0;
  private destroyed = false;
  constructor(private usersService: UsersService, private sessionService: SessionService) {}

  public get visible(): boolean { return !!this.userId && this.sessionService.isExactSuperAdmin(); }
  public get currentProducts(): RecordedUserProduct[] { return this.products.filter(product => !product.archived); }
  public get terminatedProducts(): RecordedUserProduct[] { return this.products.filter(product => product.archived); }
  ngOnChanges(): void { this.load(); }
  ngOnDestroy(): void { this.destroyed = true; ++this.generation; this.products = []; }

  public async load(): Promise<void> {
    const generation = ++this.generation;
    const userId = this.userId;
    this.products = [];
    this.state = 'idle';
    if (this.destroyed || !this.visible) { return; }
    this.state = 'loading';
    try {
      const response: any = await this.usersService.getRecordedProducts(userId);
      if (!this.isCurrent(generation, userId)) { return; }
      const data: RecordedUserProductsResponse = typeof response?.plain === 'function' ? response.plain() : response;
      // A missing/malformed response or wrong target must never become a successful empty result.
      if (data?.userId !== userId || !Array.isArray(data.products) || data.products.some(product =>
        !product?.vendorProductId || !['direct', 'dependent'].includes(product.access)
        || !product.enrollment || !product.billing || typeof product.archived !== 'boolean'
        || !Array.isArray(product.history) || !Array.isArray(product.recentActivity)
        || [...product.history, ...product.recentActivity].some(entry => !entry || typeof entry.phase !== 'string' || !entry.billing))) {
        throw new Error('Invalid product response');
      }
      this.products = data.products;
      this.state = 'loaded';
    } catch {
      if (!this.isCurrent(generation, userId)) { return; }
      this.products = [];
      this.state = 'error';
    }
  }
  private isCurrent(generation: number, userId: string): boolean {
    return !this.destroyed && generation === this.generation && userId === this.userId && this.visible;
  }
  public enrollmentLabel(product: RecordedUserProduct): string {
    return ({post: 'Enrollment recorded', terminated: 'Termination recorded', inactive: 'No completed enrollment recorded'} as Record<string, string>)[product.enrollment.state] || 'Unknown enrollment state';
  }
  public phaseLabel(phase: string): string {
    return ({pre: 'Enrollment attempt', pending: 'Pending', post: 'Enrollment recorded', error: 'Error recorded', posterror: 'Processing error recorded',
      'pre-update': 'Update attempt', 'post-update': 'Update recorded', info: 'Information recorded',
      terminated: 'Termination recorded'} as Record<string, string>)[phase] || 'Unknown phase';
  }
  public billingLabel(status: string): string {
    return ({active: 'Active snapshot', payment_failed: 'Payment failed', payment_retry: 'Payment retry',
      cancellation_pending: 'Cancellation pending', cancelled: 'Cancelled snapshot'} as Record<string, string>)[status] || 'Unknown / no usable snapshot';
  }
  public coverageLabel(coverage: string | null): string {
    return ({EMPL: 'Member Only', SPSE: 'Member plus Spouse', DPDT: 'Member plus Family',
      SPF: 'Member plus Children', PLUS1: 'Member plus Spouse or Child'} as Record<string, string>)[coverage || ''] || coverage || 'Unknown';
  }
}
